/**
 * Guide Me - the menu sidebar that appears when the user clicks the hint pill.
 *
 * Two views inside one panel:
 *
 *  1. Cards view  - one card per authored tour for the current plugin, plus a
 *                   permanent "Ask the guide" card that opens the AI prompt.
 *  2. Reader view - fetches the prose user guide HTML from the BFF training
 *                   proxy (`/api/training/user-guides/...`) and injects it as
 *                   an iframe `srcdoc`. This bypasses the nginx `X-Frame-Options:
 *                   DENY` header that would block a plain `src=` iframe, because
 *                   srcdoc documents have no HTTP response headers.
 *
 * Slides in from the right, Esc closes (or returns to cards from the reader),
 * respects `prefers-reduced-motion`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { applyLocale, pickTourLocaleKey } from '@empowernow/etl-tour';
import { useGuideMeAdapter } from './context';
import type { TourSummary } from './types';

const REDUCED_MOTION =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

export interface GuideMenuPanelProps {
  pluginLabel: string;
  pluginIcon?: string;
  /**
   * Authored tour summaries for this plugin. Bodies are loaded on demand
   * (intent triggers in {@link GuideMeRoot}); the menu renders synchronously
   * from these summaries and upgrades to localized titles + step counts as
   * the lazy preload settles.
   */
  tours: ReadonlyArray<TourSummary>;
  /** Current pathname; used to mark the tour matching the current route. */
  pathname: string;
  /**
   * Absolute URL of the plugin-level "hub" prose guide (HTML), if any.
   * When set, the menu shows a "📖 Open user guide" header button that swaps
   * the panel into reader view.
   */
  pluginGuideUrl?: string | null;
  onSelectTour: (tour: TourSummary) => void;
  onClose: () => void;
  /**
   * Experience shell locale (`LocalizationContext` `resolvedLocale`).
   * Tour card copy uses `tour.locales` when a matching overlay was merged.
   */
  appLocale: string;
}

interface ReaderState {
  url: string;
  /** Friendly title shown above the iframe. */
  title: string;
}

type Tab = 'tours' | 'search';

interface SearchHit {
  readonly tour: TourSummary;
  readonly score: number;
  readonly matchedTerms: ReadonlyArray<string>;
}

export function GuideMenuPanel({
  pluginLabel,
  pluginIcon,
  tours,
  pathname,
  pluginGuideUrl,
  onSelectTour,
  onClose,
  appLocale,
}: GuideMenuPanelProps) {
  const adapter = useGuideMeAdapter();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const [reader, setReader] = useState<ReaderState | null>(null);
  const [tab, setTab] = useState<Tab>('tours');
  const searchEnabled = typeof adapter.searchTours === 'function';

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Esc behaviour:
  //  - in reader view → return to cards view
  //  - in cards view  → close the whole panel
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      if (reader) {
        setReader(null);
      } else {
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, reader]);

  const currentTourId = useMemo<string | null>(() => {
    const exact = tours.find((t) => t.match?.route && pathname.startsWith(t.match.route));
    return exact?.id ?? null;
  }, [tours, pathname]);

  const handleBackdropClick = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      if (ev.target === ev.currentTarget) onClose();
    },
    [onClose],
  );

  const openPluginGuide = useCallback(() => {
    if (!pluginGuideUrl) return;
    setReader({ url: pluginGuideUrl, title: `${pluginLabel} user guide` });
  }, [pluginGuideUrl, pluginLabel]);

  const openTourGuide = useCallback(
    (tour: TourSummary) => {
      const url = adapter.buildGuideUrl(tour.guidePath);
      if (!url) return;
      setReader({ url, title: tour.title });
    },
    [adapter],
  );

  const closeReader = useCallback(() => setReader(null), []);

  if (typeof document === 'undefined') return null;

  const panel = (
    <div
      data-testid="guide-me-menu"
      role="dialog"
      aria-modal="true"
      aria-label={`Guide Me - ${pluginLabel}`}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999985,
        // Slightly deeper scrim so the dark drawer reads as an intentional
        // "assistant surface" floating above the (often light) app, not an
        // unthemed slab.
        background: 'rgba(8, 12, 24, 0.55)',
        display: 'flex',
        justifyContent: 'flex-end',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: reader ? 'min(640px, 100vw)' : 'min(420px, 100vw)',
          height: '100vh',
          background:
            'linear-gradient(180deg, #1f1750 0%, #1a1444 45%, #120d33 100%)',
          color: 'white',
          borderLeft: '2px solid rgba(167, 139, 250, 0.6)',
          // Neutral elevation shadow (not a violet glow) so the panel lifts
          // cleanly off a light background instead of smearing purple onto it,
          // plus a crisp violet hairline ring that keeps the Guide Me identity.
          boxShadow:
            '-24px 0 60px rgba(6, 4, 24, 0.5), -1px 0 0 1px rgba(196, 181, 253, 0.4), inset 1px 0 0 rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
          animation: REDUCED_MOTION ? undefined : 'guideMeMenuIn 220ms cubic-bezier(0.22,1,0.36,1) both',
          transition: REDUCED_MOTION ? 'none' : 'width 220ms ease-out',
        }}
      >
        {/* Signature accent strip — matches the orb halo so users instantly tie this panel to Guide Me */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background:
              'linear-gradient(90deg, #f59e0b 0%, #ef4444 22%, #ec4899 44%, #8b5cf6 66%, #3b82f6 88%, #10b981 100%)',
            zIndex: 1,
          }}
        />
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '20px 18px 14px',
            borderBottom: '1px solid rgba(167, 139, 250, 0.28)',
            flexShrink: 0,
          }}
        >
          {reader && (
            <button
              type="button"
              onClick={closeReader}
              aria-label="Back to walkthroughs"
              data-testid="guide-me-menu-back"
              style={iconButtonStyle}
            >
              ←
            </button>
          )}
          <span
            aria-hidden="true"
            style={{
              fontSize: 22,
              width: 38,
              height: 38,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(99,102,241,0.18)',
              borderRadius: 10,
              flexShrink: 0,
            }}
          >
            {reader ? '📖' : pluginIcon ?? '💡'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'rgba(165, 180, 252, 0.95)',
                fontWeight: 700,
              }}
            >
              {reader ? 'User guide' : 'Guide Me'}
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                // Explicit light color: the parent wrapper has no color, so the
                // shell's `[data-theme] div { color: var(--main-text) }` rule
                // would otherwise darken this to invisible on the dark panel in
                // light themes. `inherit` can't help here (the wrapper is also
                // darkened), so pin a literal.
                color: 'white',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {reader ? reader.title : pluginLabel}
            </div>
          </div>
          {reader && (
            <a
              href={reader.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guide-me-reader-newtab"
              aria-label="Open guide in new tab"
              style={{
                ...iconButtonStyle,
                textDecoration: 'none',
                fontSize: 12,
              }}
              title="Open in new tab"
            >
              ↗
            </a>
          )}
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close Guide Me menu"
            data-testid="guide-me-menu-close"
            style={iconButtonStyle}
          >
            ✕
          </button>
        </div>

        {/* Tab bar — hidden in reader view, and only shown when the host
            wired a `searchTours` implementation. Keeps single-tab hosts
            visually identical to the pre-search layout. */}
        {!reader && searchEnabled && (
          <TabBar tab={tab} onChange={setTab} />
        )}

        {/* Body */}
        {reader ? (
          <ReaderView url={reader.url} />
        ) : tab === 'search' && searchEnabled ? (
          <SearchView
            search={adapter.searchTours!}
            prepareSearch={adapter.prepareSearch}
            onSelectTour={onSelectTour}
            appLocale={appLocale}
          />
        ) : (
          <CardsView
            tours={tours}
            currentTourId={currentTourId}
            pluginGuideUrl={pluginGuideUrl ?? null}
            onSelectTour={onSelectTour}
            onOpenPluginGuide={openPluginGuide}
            onOpenTourGuide={openTourGuide}
            appLocale={appLocale}
          />
        )}

        {/* Footer */}
        <div
          style={{
            padding: '10px 18px 14px',
            fontSize: 11,
            color: 'rgba(196, 181, 253, 0.78)',
            borderTop: '1px solid rgba(167, 139, 250, 0.22)',
            flexShrink: 0,
          }}
        >
          {reader ? (
            <>
              Press <kbd style={kbdStyle}>Esc</kbd> to return to walkthroughs. The guide opens
              in a new tab if you want it side-by-side with a tour.
            </>
          ) : (
            <>
              Press <kbd style={kbdStyle}>Esc</kbd> to close. Tours run in dry-run by default —
              no data is changed without your final click.
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

/* -------------------------------------------------------------------------- */
/* Cards view                                                                  */
/* -------------------------------------------------------------------------- */

interface CardsViewProps {
  tours: ReadonlyArray<TourSummary>;
  currentTourId: string | null;
  pluginGuideUrl: string | null;
  onSelectTour: (tour: TourSummary) => void;
  onOpenPluginGuide: () => void;
  onOpenTourGuide: (tour: TourSummary) => void;
  appLocale: string;
}

function CardsView({
  tours,
  currentTourId,
  pluginGuideUrl,
  onSelectTour,
  onOpenPluginGuide,
  onOpenTourGuide,
  appLocale,
}: CardsViewProps) {
  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '14px 14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 12, color: 'rgba(203, 213, 225, 0.85)', padding: '0 4px 4px' }}>
        Pick a guided walkthrough — the cursor will move for you and stop before any destructive action.
      </div>

      {pluginGuideUrl && (
        <button
          type="button"
          data-testid="guide-me-menu-read-guide"
          onClick={onOpenPluginGuide}
          style={{
            ...cardBaseStyle,
            background:
              'linear-gradient(135deg, rgba(76, 49, 156, 0.55) 0%, rgba(48, 32, 110, 0.55) 100%)',
            borderColor: 'rgba(167, 139, 250, 0.4)',
          }}
        >
          <span aria-hidden="true" style={iconChipStyle}>
            📖
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
            {/* Literal color: parent wrapper carries no color, so the shell's
                `[data-theme] div` rule would darken this title to invisible. */}
            <div style={{ fontSize: 14, fontWeight: 600, color: 'white' }}>Read the user guide</div>
            <div style={{ fontSize: 12, color: 'rgba(203, 213, 225, 0.9)' }}>
              The full prose docs for this plugin, rendered inline.
            </div>
          </div>
          <span aria-hidden="true" style={chevronStyle}>
            ›
          </span>
        </button>
      )}

      {tours.map((tour) => (
        <TourCard
          key={tour.id}
          tour={tour}
          isCurrent={tour.id === currentTourId}
          onClick={() => onSelectTour(tour)}
          onReadGuide={tour.guidePath ? () => onOpenTourGuide(tour) : undefined}
          appLocale={appLocale}
        />
      ))}

      {/* Ask the guide (Phase 2 / AI agent) — temporarily hidden. */}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab bar                                                                     */
/* -------------------------------------------------------------------------- */

function TabBar({ tab, onChange }: { tab: Tab; onChange: (next: Tab) => void }) {
  const items: ReadonlyArray<readonly [Tab, string, string]> = [
    ['tours', 'Tours', 'guide-me-tab-tours'],
    ['search', 'Search', 'guide-me-tab-search'],
  ];
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: 6,
        padding: '10px 14px 0',
        borderBottom: '1px solid rgba(167, 139, 250, 0.18)',
        flexShrink: 0,
      }}
    >
      {items.map(([id, label, testId]) => {
        const active = tab === id;
        return (
          <button
            key={id}
            role="tab"
            type="button"
            data-testid={testId}
            aria-selected={active}
            onClick={() => onChange(id)}
            style={{
              appearance: 'none',
              background: 'transparent',
              color: active ? 'white' : 'rgba(196, 181, 253, 0.78)',
              border: 'none',
              borderBottom: `2px solid ${
                active ? 'rgba(196, 181, 253, 0.95)' : 'transparent'
              }`,
              padding: '8px 10px',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'color 120ms, border-color 120ms',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Search view                                                                 */
/* -------------------------------------------------------------------------- */

interface SearchViewProps {
  search: NonNullable<ReturnType<typeof useGuideMeAdapter>['searchTours']>;
  prepareSearch?: ReturnType<typeof useGuideMeAdapter>['prepareSearch'];
  onSelectTour: (tour: TourSummary) => void;
  appLocale: string;
}

function SearchView({ search, prepareSearch, onSelectTour, appLocale }: SearchViewProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  // The query is debounced just enough that prefix expansion runs on a
  // settled keystroke instead of every input event — search itself is fast,
  // but rendering 20 cards per keystroke is wasteful.
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
    // Hint to the host that it should warm any heavier index (e.g. narration)
    // it lazy-loads. Idempotent. Re-runs each mount, which is at most once
    // per panel-open in practice — the SearchView is unmounted when the
    // panel closes, not when the user toggles between tabs we keep cheap.
    prepareSearch?.();
  }, [prepareSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 80);
    return () => clearTimeout(t);
  }, [query]);

  const hits = useMemo<ReadonlyArray<SearchHit>>(() => {
    const trimmed = debounced.trim();
    if (trimmed.length < 2) return [];
    return search(trimmed, { limit: 12, prefixLastTerm: true, fuzzy: true });
  }, [debounced, search]);

  const showHints = query.trim().length < 2;
  const showEmpty = !showHints && hits.length === 0;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div style={{ padding: '12px 14px 10px', flexShrink: 0 }}>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search every tour — try “reset password”"
          data-testid="guide-me-search-input"
          aria-label="Search tours"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid rgba(167, 139, 250, 0.45)',
            background: 'rgba(15, 12, 38, 0.7)',
            color: 'white',
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '4px 14px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {showHints && (
          <div
            style={{
              fontSize: 12,
              color: 'rgba(203, 213, 225, 0.85)',
              padding: '4px 4px 0',
              lineHeight: 1.55,
            }}
          >
            Search across every plugin. Try{' '}
            <em>reset password</em>, <em>register oauth client</em>, or{' '}
            <em>find a user</em>. Tours run in dry-run by default.
          </div>
        )}
        {showEmpty && (
          <div
            data-testid="guide-me-search-empty"
            style={{
              fontSize: 12,
              color: 'rgba(203, 213, 225, 0.85)',
              padding: '8px 4px',
            }}
          >
            No tours match “{query.trim()}”. Try a shorter or simpler phrase.
          </div>
        )}
        {hits.map((hit) => (
          <TourCard
            key={hit.tour.id}
            tour={hit.tour}
            isCurrent={false}
            onClick={() => onSelectTour(hit.tour)}
            appLocale={appLocale}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reader view                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetches the guide HTML via the nginx `/api/` proxy (which forwards to BFF)
 * and injects it as `srcdoc`. This sidesteps the `X-Frame-Options: DENY` +
 * `frame-ancestors 'none'` headers that nginx attaches to proxied responses —
 * those headers only affect HTTP-delivered iframe content, not srcdoc.
 */
function ReaderView({ url }: { url: string }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; html: string }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        return res.text();
      })
      .then((html) => {
        if (!cancelled) setState({ kind: 'ok', html });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setState({ kind: 'error', message: (err as Error).message ?? String(err) });
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: '#0b1220',
        minHeight: 0,
        position: 'relative',
      }}
      data-testid="guide-me-menu-reader"
    >
      {state.kind === 'loading' && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(148,163,184,0.7)', fontSize: 13,
          fontFamily: 'system-ui, sans-serif',
          gap: 8,
        }}>
          <span style={{ fontSize: 18 }}>📄</span>
          Loading guide…
        </div>
      )}

      {state.kind === 'error' && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 10,
          color: 'rgba(252,165,165,0.85)', fontSize: 13,
          fontFamily: 'system-ui, sans-serif', padding: 24, textAlign: 'center',
        }}>
          <span style={{ fontSize: 28 }}>⚠️</span>
          {/* `color: inherit` inline beats the shell's non-!important
              `[data-theme] span` rule, keeping the light text on this dark panel. */}
          <span style={{ color: 'inherit' }}>Could not load the guide.</span>
          <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>{state.message}</span>
        </div>
      )}

      {state.kind === 'ok' && (
        /* srcdoc creates a synthetic document — no HTTP response headers,
           so X-Frame-Options / frame-ancestors restrictions don't apply. */
        <iframe
          srcDoc={state.html}
          title="User guide"
          sandbox="allow-scripts allow-popups"
          style={{
            flex: 1,
            width: '100%',
            height: '100%',
            border: 'none',
            background: 'white',
          }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tour card                                                                   */
/* -------------------------------------------------------------------------- */

function TourCard({
  tour,
  isCurrent,
  onClick,
  onReadGuide,
  appLocale,
}: {
  tour: TourSummary;
  isCurrent: boolean;
  onClick: () => void;
  onReadGuide?: () => void;
  appLocale: string;
}) {
  const adapter = useGuideMeAdapter();
  // If the tour body has been preloaded by an intent trigger, we get a
  // localized title + summary + accurate step count. Otherwise we render
  // the manifest fields, which is enough for the user to choose a tour.
  const cached = adapter.getCachedTour(tour.id);
  const localeKey = useMemo(
    () => (cached ? pickTourLocaleKey(appLocale, cached.locales) : undefined),
    [cached, appLocale],
  );
  const displayTour = useMemo(() => {
    if (!cached) {
      return { title: tour.title, summary: tour.summary };
    }
    const localized = applyLocale(cached, localeKey);
    return { title: localized.title, summary: localized.summary };
  }, [cached, localeKey, tour.title, tour.summary]);
  const stepCount = cached?.steps.length ?? tour.stepCount;
  const icon = useMemo(() => pickTourIcon(tour), [tour]);
  // The whole card is the "Start tour" affordance. The "📖 Read" button is a
  // sibling rendered in the same row so we never end up with nested buttons
  // (HTML doesn't allow that). Clicking Read opens the prose for this tour
  // inside the sidebar's reader view without starting the walkthrough.
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        borderRadius: 12,
        border: '1px solid',
        borderColor: isCurrent ? 'rgba(196, 181, 253, 0.7)' : 'rgba(167, 139, 250, 0.35)',
        background: isCurrent
          ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.45) 0%, rgba(167, 139, 250, 0.28) 100%)'
          : 'linear-gradient(135deg, rgba(76, 49, 156, 0.5) 0%, rgba(48, 32, 110, 0.5) 100%)',
        boxShadow: isCurrent
          ? '0 0 0 1px rgba(196, 181, 253, 0.25), 0 8px 22px rgba(124, 58, 237, 0.25)'
          : '0 4px 14px rgba(8, 4, 28, 0.35)',
        overflow: 'hidden',
        transition: 'background 150ms, border-color 150ms, transform 150ms, box-shadow 150ms',
      }}
    >
      <button
        type="button"
        data-testid={`guide-me-menu-card-${tour.id}`}
        onClick={onClick}
        style={{
          ...cardBaseStyle,
          gap: 14,
          alignItems: 'flex-start',
          padding: '14px 14px 12px',
          background: 'transparent',
          border: 'none',
          borderRadius: 0,
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontSize: 22,
            width: 42,
            height: 42,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isCurrent
              ? 'rgba(99,102,241,0.35)'
              : 'rgba(15, 23, 42, 0.6)',
            border: `1px solid ${isCurrent ? 'rgba(165,180,252,0.5)' : 'rgba(148,163,184,0.25)'}`,
            borderRadius: 10,
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          {icon}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'white',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {displayTour.title}
          </div>
          {displayTour.summary && (
            <div
              style={{
                fontSize: 12,
                color: 'rgba(203, 213, 225, 0.85)',
                lineHeight: 1.45,
                wordBreak: 'break-word',
                // Let the summary flow to its natural height — these cards
                // sit in a vertically scrollable list, so growing them is
                // free. Line-clamp here was clipping the second line at the
                // baseline mid-glyph and crowding out the step-count row.
              }}
            >
              {displayTour.summary}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 2,
              fontSize: 11,
              color: 'rgba(148, 163, 184, 0.75)',
            }}
          >
            {/* `color: inherit` inline beats the shell's non-!important
                `[data-theme] span` rule, keeping the light text on this dark card. */}
            <span style={{ color: 'inherit' }}>
              {stepCount === undefined
                ? 'Loading…'
                : `${stepCount} step${stepCount === 1 ? '' : 's'}`}
            </span>
            {isCurrent && (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'rgba(99,102,241,0.4)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 4,
                  lineHeight: 1.2,
                }}
              >
                On this page
              </span>
            )}
          </div>
        </div>
        <span aria-hidden="true" style={{ ...chevronStyle, alignSelf: 'center' }}>
          ›
        </span>
      </button>

      {onReadGuide && (
        <button
          type="button"
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            onReadGuide();
          }}
          aria-label={`Read the user guide for ${tour.title}`}
          data-testid={`guide-me-menu-read-${tour.id}`}
          title="Read the prose guide for this task"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            padding: '8px 12px',
            background: 'rgba(15, 23, 42, 0.55)',
            color: 'rgba(165,180,252,0.95)',
            border: 'none',
            borderLeft: '1px solid rgba(99,102,241,0.22)',
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: 'inherit',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">📖</span>
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared styles                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Picks a content-relevant emoji for a tour card from the tour's id /
 * title / summary. Keyword matches are ordered specific → generic, so
 * "reset password" wins over the bare "user" fallback. Authors can pin
 * an explicit icon later via the manifest if the heuristic guesses wrong;
 * for now the lookup keeps the card visuals self-maintaining as new
 * tours are added.
 */
function pickTourIcon(tour: TourSummary): string {
  const haystack = `${tour.id} ${tour.title} ${tour.summary ?? ''}`.toLowerCase();
  const rules: ReadonlyArray<readonly [RegExp, string]> = [
    [/\breset\s*password|forgot\s*password\b/, '🔑'],
    [/\bdisable|deactivate|suspend|block\b/,    '🚫'],
    [/\bdelete|remove|destroy\b/,               '🗑️'],
    [/\blink|associate|connect|attach\b/,       '🔗'],
    [/\bunlink|detach|disconnect\b/,            '🔓'],
    [/\bcreate|register|add\b/,                 '✨'],
    [/\bfind|search|filter|lookup\b/,           '🔍'],
    [/\btour|orient|walkthrough|overview\b/,    '🧭'],
    [/\binvite|onboard\b/,                      '📨'],
    [/\btoken|api\s*key|pat\b/,                 '🎫'],
    [/\bsecret|vault|credential\b/,             '🔐'],
    [/\bclient|oauth|sso|saml|oidc\b/,          '🔐'],
    [/\brole|permission|access|policy\b/,       '🛡️'],
    [/\bgroup|collection|team\b/,               '👥'],
    [/\bperson|user|account|identity\b/,        '👤'],
    [/\breport|analytics|insight|dashboard\b/,  '📊'],
    [/\bsettings|configure|preferences\b/,      '⚙️'],
    [/\bnotification|alert\b/,                  '🔔'],
    [/\bworkflow|automation|run\b/,             '⚡'],
    [/\bapprov|review\b/,                       '✅'],
  ];
  for (const [re, emoji] of rules) {
    if (re.test(haystack)) return emoji;
  }
  return '▶';
}

const cardBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  textAlign: 'left',
  padding: '12px 14px',
  border: '1px solid rgba(167, 139, 250, 0.32)',
  borderRadius: 12,
  color: 'white',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 150ms, border-color 150ms, transform 150ms',
};

const iconButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(76, 49, 156, 0.65)',
  color: 'white',
  border: '1px solid rgba(167, 139, 250, 0.45)',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  flexShrink: 0,
};

const iconChipStyle: React.CSSProperties = {
  fontSize: 22,
  width: 38,
  height: 38,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.35), rgba(124, 58, 237, 0.35))',
  border: '1px solid rgba(196, 181, 253, 0.35)',
  borderRadius: 10,
  flexShrink: 0,
};

const chevronStyle: React.CSSProperties = {
  fontSize: 18,
  color: 'rgba(196, 181, 253, 0.95)',
  flexShrink: 0,
};

const kbdStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  fontSize: 10,
  background: 'rgba(48, 32, 110, 0.85)',
  color: 'rgba(237, 233, 254, 0.95)',
  padding: '1px 5px',
  borderRadius: 4,
  border: '1px solid rgba(167, 139, 250, 0.5)',
};

/** Inject the panel slide-in keyframe (only once). */
export function ensureGuideMeMenuStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('guide-me-menu-styles')) return;
  const style = document.createElement('style');
  style.id = 'guide-me-menu-styles';
  style.textContent = `
    @keyframes guideMeMenuIn {
      0%   { transform: translateX(8%); opacity: 0; }
      100% { transform: translateX(0);   opacity: 1; }
    }
    [data-testid="guide-me-menu"] button:hover,
    [data-testid="guide-me-menu"] a:hover {
      background: rgba(139, 92, 246, 0.32);
      border-color: rgba(196, 181, 253, 0.7);
    }
    [data-testid="guide-me-menu"] button:focus-visible,
    [data-testid="guide-me-menu"] a:focus-visible {
      outline: 2px solid rgba(196, 181, 253, 0.95);
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(style);
}
